export const PATH = {
    HOME: '/',
    LOGIN: '/login',
    /**
     * The onboarding wizard — a new organisation's first-run setup, from the
     * welcome to the catalog import and Finish.
     *
     * **Full-screen**: no sidebar, no navbar. The wizard is the only thing an
     * organisation that has not finished onboarding can do, so a shell would
     * offer a menu of screens it cannot use yet. `useLayout` exempts it by
     * prefix, so every step under it is full-screen too; how the steps are
     * addressed is settled with the wizard frame (APP-165).
     */
    ONBOARDING: '/onboarding',
    /**
     * One wizard step: a route segment under ONBOARDING (`/onboarding/start`,
     * `/onboarding/products`, …), so each step has an address, and Back and a
     * reload keep it. The step list is `ONBOARDING_STEPS` in the onboarding
     * page's strip meta; the route itself is ONBOARDING's `/*` splat.
     */
    ONBOARDING_STEP: {
        PATTERN: '/onboarding/:step',
        BIND: (step: string) => `/onboarding/${step}`,
    },
    /**
     * Where a user who is not an OrgAdmin lands while their organisation is
     * still onboarding: there is nothing for them to do until the owner
     * finishes. Full-screen, for the wizard's reason.
     */
    SETUP_PENDING: '/setup-pending',
    /**
     * The Control Center — the app's home, and the first row of the sidebar.
     *
     * It has **two addresses on purpose**, `/` and this one, wired as an index
     * route rather than a redirect so neither rewrites the other: `/` is where
     * the app lands and what a bookmark of "the app" points at, and the named
     * path is what a link to this screen from inside the app should say.
     *
     * This is the dashboard that `/` used to lack. Until it existed, `/`
     * redirected to `/sales` and the sidebar carried no row for home at all —
     * a "Dashboard" item would have highlighted Sales (BOS-170).
     */
    CONTROL_CENTER: '/control-center',
    /**
     * The Branches area — a tab shell, same shape as the `/accounting` ones:
     * every `/branches*` URL renders the shell, and each tab is a child route,
     * so the selected tab and the address bar are the same fact.
     *
     * Unlike those shells the default tab has **no path of its own**: the
     * branch list is reached at the bare ROOT through an index route, and that
     * is its only URL. Books gives its default tab both `/accounting/books` and
     * `/accounting/books/accounts` because four report tabs read as peers and
     * one of them having no address would be the odd one out. Here there are
     * two tabs and the area is named after the first, so `/branches/branches`
     * would be a second name for a screen that already has the obvious one.
     */
    BRANCHES: {
        ROOT: '/branches',
        /**
         * The shift register — the till-side record of who opened a drawer,
         * what they counted back in, and what the ledger says the shift took.
         *
         * A tab of Branches rather than of Sales, which is where it used to
         * live. Sales is a dashboard of parallel widgets over a period; this is
         * a filtered list you drill into, scoped by branch, and a shift belongs
         * to the till that ran it. Grouped with the branch it happened at, the
         * two tabs answer the same question at two depths — which branches
         * exist, and what happened at one of them.
         */
        SHIFTS: '/branches/shifts',
        /**
         * One branch's catalog import — the wizard's import flow inside the
         * shell, for a business that is already running.
         *
         * **Not a tab.** It sits outside the Branches tab shell, like manual
         * import sits outside Purchases: it is a multi-step form about one
         * branch, and a tab bar above a half-reviewed file offers two ways to
         * lose it. Its navbar back button goes to ROOT (`navigation.ts`).
         */
        IMPORT: '/branches/:branchId/import',
        BIND_IMPORT: (branchId: string) => `/branches/${branchId}/import`,
        /**
         * One step of a branch's import: a route segment under IMPORT
         * (`/branches/<id>/import/file`, `…/names`, `…/products`,
         * `…/result`), as the wizard's steps are under ONBOARDING, so Back
         * and a reload keep the step and the import's lock holds a step
         * change while an import runs. The route is IMPORT's `/*` splat; the
         * bare IMPORT path opens File.
         */
        IMPORT_STEP: {
            PATTERN: '/branches/:branchId/import/:step',
            BIND: (branchId: string, step: string) =>
                `/branches/${branchId}/import/${step}`,
        },
    },
    /**
     * Where the shift register used to live. Kept as a redirect, not a page:
     * the URL is in bookmarks and in support threads, and a 404 is a worse
     * answer than the screen it moved to. See `AppRouter`.
     */
    LEGACY_SALES_SHIFTS: '/sales/shifts',
    CATEGORIES: '/categories',
    ATTRIBUTES: '/attributes',
    PRODUCTS: {
        ROOT: '/products',
        NEW: '/products/new',
        DETAIL: '/products/:productId',
        EDIT: '/products/:productId/edit',
        BIND: (productId: string) => `/products/${productId}`,
        BIND_EDIT: (productId: string) => `/products/${productId}/edit`,
    },
    SUPPLIERS: '/suppliers',
    BRANDS: '/brands',
    PURCHASES: {
        ROOT: '/purchases',
        MANUAL_IMPORT: '/purchases/manual-import',
        MANUAL_IMPORT_EDIT: '/purchases/manual-import/:draftId',
        BIND_MANUAL_IMPORT_EDIT: (draftId: string) =>
            `/purchases/manual-import/${draftId}`,
        FILE_IMPORT: '/purchases/file-import',
    },
    SALES: '/sales',
    TEAM: '/team',
    /**
     * The Analytics area. One nav item, one tab shell, same shape as the
     * `/accounting` shells: every `/analytics*` URL renders the shell, and each
     * tab is a child route, so the selected tab and the address bar are the
     * same fact.
     *
     * Separate from `/accounting` deliberately. The ledger screens report what
     * was *recorded*; these read across it. The bare ROOT is the same screen as
     * FINANCIAL_POSITION — an index route, not a redirect, so both URLs work
     * and neither rewrites the other.
     */
    ANALYTICS: {
        ROOT: '/analytics',
        FINANCIAL_POSITION: '/analytics/financial-position',
        REPORTING: '/analytics/reporting',
    },
    /**
     * The Accounting area. Groups the ledger screens behind five nav items —
     * Books, Statements, Registers, Operations, Configurations — each a tab
     * shell, instead of one sidebar entry per report.
     *
     * ROOT is a prefix, not a route: nothing renders `/accounting` itself, so
     * it falls through to the not-found page.
     */
    ACCOUNTING: {
        ROOT: '/accounting',
        BOOKS: {
            /**
             * The tab shell. Every `/accounting/books*` URL renders it; the
             * four tabs below are its child routes. The bare ROOT is the same
             * screen as ACCOUNTS — an index route, not a redirect, so both
             * URLs work and neither rewrites the other.
             */
            ROOT: '/accounting/books',
            ACCOUNTS: '/accounting/books/accounts',
            JOURNAL: '/accounting/books/journal',
            TRIAL_BALANCE: '/accounting/books/trial-balance',
            VAT: '/accounting/books/vat',
            /**
             * Drill from a tab to the **Journal tab**, scoped to one account.
             *
             * `range` is the shell's current window, carried across so the
             * picker above reads the same after the jump as before it. Omitting
             * it would drop the tab back to its default month mid-drill, which
             * is the "right account, no entries" dead link this affordance
             * exists to avoid.
             */
            BIND_JOURNAL_FOR_ACCOUNT: (
                accountId: string,
                range?: { from: string; to: string },
            ) =>
                `/accounting/books/journal?account=${accountId}${
                    range ? `&from=${range.from}&to=${range.to}` : ''
                }`,
            /**
             * Open the Journal tab on one entry — the audit-trail link from a
             * year-end close, rollover or reversal.
             *
             * `range` is **required**, and that is the whole point. The entry
             * has to be in the list behind the drawer whatever year it is dated
             * in, but the tab cannot widen its own window: the window belongs
             * to the Books shell, and widening it silently would leave the
             * picker above describing a list it no longer matches. So the
             * caller has to say which window shows the entry — pass the entry's
             * own date on both sides and the answer is always yes.
             */
            BIND_JOURNAL_FOR_ENTRY: (
                entryId: string,
                range: { from: string; to: string },
            ) =>
                `/accounting/books/journal?entry=${entryId}&from=${range.from}&to=${range.to}`,
        },
        /**
         * The published outputs: what you would hand to someone outside the
         * business. Carries a date range like BOOKS — and interprets it the
         * same way, Balance Sheet taking the end and Profit & Loss the whole
         * window. See `statementsOutletContext` for why that split exists.
         */
        STATEMENTS: {
            ROOT: '/accounting/statements',
            BALANCE_SHEET: '/accounting/statements/balance-sheet',
            PROFIT_LOSS: '/accounting/statements/profit-loss',
        },
        /**
         * The subledgers: a register of things (assets, employees,
         * shareholders) plus the runs that post them into the ledger. No date
         * range — "the list of employees" is not read over a window, and each
         * run carries its own period.
         */
        REGISTERS: {
            ROOT: '/accounting/registers',
            FIXED_ASSETS: '/accounting/registers/fixed-assets',
            SALARIES: '/accounting/registers/salaries',
            DIVIDENDS: '/accounting/registers/dividends',
        },
        /**
         * The operations shell: the catalog of every way the ledger gets
         * written to, the form that records one, and the log of what was
         * recorded. Same tab-per-child-route shape as REGISTERS, and no date
         * range for the same reason — "the list of operations" is not read over
         * a window, and a recorded operation carries its own date.
         *
         * Sits between REGISTERS and CONFIGURATIONS: an operation is a thing
         * you do, like a register entry, but the templates behind it are
         * configuration — so it belongs at the seam.
         */
        OPERATIONS: {
            ROOT: '/accounting/operations',
            TEMPLATES: '/accounting/operations/templates',
            RECORD: '/accounting/operations/record',
            ACTIVITY: '/accounting/operations/activity',
            /**
             * The template editor. A standalone sub-page **outside** the tab
             * shell, like `/purchases/manual-import`: it is a form, not a view
             * of the catalog, and a tab bar over a half-finished recipe offers
             * three ways to lose it. The navbar gives it a back button to the
             * Templates tab instead.
             *
             * Keyed on the template's `key`, not its uuid — the key is what the
             * seed file, the API and any support conversation call the
             * operation, so it is what the URL should carry.
             */
            EDIT: '/accounting/operations/:key/edit',
            BIND_EDIT: (key: string) =>
                `/accounting/operations/${encodeURIComponent(key)}/edit`,
            /**
             * The create form. A separate route from `EDIT` rather than a
             * sentinel key, for the same reason `EDIT` is standalone: `:key`
             * there is documented as the real template key used everywhere
             * downstream, and a route that could also mean "there is no key
             * yet" would be a second thing every reader of it has to hold in
             * mind.
             */
            NEW: '/accounting/operations/new',
            /**
             * The category management screen — rename, add and remove the
             * taxonomy every operation is filed under.
             *
             * A standalone sub-page outside the tab shell, like `EDIT`: it
             * configures the *catalog*, not one operation, and a fourth tab
             * would give a six-row list the same weight as the catalog itself.
             * Reached from the Templates tab's toolbar; the navbar gives it a
             * back button there.
             */
            CATEGORIES: '/accounting/operations/categories',
            /**
             * The version history — the **Templates tab with its drawer open**,
             * not a page of its own.
             *
             * The history answers a question about the screen a reader is
             * already on, so it is a drawer both the catalog and the editor
             * mount their own copy of; it stopped needing an owner the moment it
             * owned its own read. The key stays in the URL here for the reason
             * `BIND_ACTIVITY_FOR_KEY` carries its filter: a link that survives a
             * refresh and can be pasted into a support thread.
             */
            BIND_HISTORY: (key: string) =>
                `/accounting/operations/templates?history=${encodeURIComponent(key)}`,
            /**
             * The Activity tab, pre-filtered to one operation — the link a
             * version card's "entries posted under this version" carries.
             *
             * The filter goes in the URL rather than in a handoff, so the link
             * survives a refresh and can be pasted into a support thread. The
             * tab reads it once on mount and owns its filters from then on.
             */
            BIND_ACTIVITY_FOR_KEY: (key: string) =>
                `/accounting/operations/activity?key=${encodeURIComponent(key)}`,
        },
        /**
         * The Accounting shell for the screens that *configure* the ledger
         * rather than report on it. Same tab-per-child-route shape as BOOKS,
         * but no date range — a fiscal calendar and a set of tax rates are not
         * read over a window.
         */
        CONFIGURATIONS: {
            ROOT: '/accounting/configurations',
            PERIODS: '/accounting/configurations/periods',
            SETTINGS: '/accounting/configurations/settings',
        },
    },
    NOT_FOUND: '/404',
};
